import type { CotEventParams } from '../types/cot.js';
import { DEFAULT_STALE_MS } from '../constants/stale-times.js';
import { escapeXml, xmlAttr } from './xml-helpers.js';

/**
 * Build a CoT event XML string from typed parameters.
 * Replaces the inline template string from the prototype's app.js.
 */
export function buildCotEventXml(params: CotEventParams): string {
  const now = new Date().toISOString();
  const staleMs = (params.staleMinutes ?? 5) * 60 * 1000;
  const stale = new Date(Date.now() + staleMs).toISOString();

  const hae = params.hae ?? 0;
  const ce = params.ce ?? 9999999;
  const le = params.le ?? 9999999;
  const how = params.how ?? 'h-g-i-g-o';
  const groupName = params.groupName ?? 'Cyan';
  const groupRole = params.groupRole ?? 'Team Member';

  const remarksTag = params.remarks
    ? `\n    <remarks>${escapeXml(params.remarks)}</remarks>`
    : '';
  const simTag = params.exercise ? '    <__sim exercise="true" />' : '';

  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<event ${buildEventAttrs(params, now, stale, how)}>`,
    `  <point ${buildPointAttrs(params.lat, params.lon, hae, ce, le)} />`,
    '  <detail>',
    `    <contact ${xmlAttr('callsign', params.callsign)} />`,
    remarksTag,
    '    <precisionlocation altsrc="???" geopointsrc="USER" />',
    `    <__group ${xmlAttr('name', groupName)} ${xmlAttr('role', groupRole)} />`,
    simTag,
    '  </detail>',
    '</event>',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Build the <event> element's attribute string */
function buildEventAttrs(
  params: CotEventParams,
  now: string,
  stale: string,
  how: string,
): string {
  return [
    xmlAttr('version', '2.0'),
    xmlAttr('uid', params.uid),
    xmlAttr('type', params.type),
    xmlAttr('time', now),
    xmlAttr('start', now),
    xmlAttr('stale', stale),
    xmlAttr('how', how),
  ].join(' ');
}

/** Build the <point> element's attribute string */
function buildPointAttrs(
  lat: number,
  lon: number,
  hae: number,
  ce: number,
  le: number,
): string {
  return [
    xmlAttr('lat', lat),
    xmlAttr('lon', lon),
    xmlAttr('hae', hae),
    xmlAttr('ce', ce),
    xmlAttr('le', le),
  ].join(' ');
}

/** Compute a stale timestamp from now */
export function computeStaleTime(staleMs: number = DEFAULT_STALE_MS): string {
  return new Date(Date.now() + staleMs).toISOString();
}
